import React from "react";
import Seo from "../components/Seo";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PageHero from "../components/PageHero";
import { Reveal } from "../lib/motion";
import { useLang } from "../i18n/LanguageContext";
import { IMAGES, WHY, PROCESS } from "../data";

export default function About() {
  const { t } = useLang();
  return (
    <div data-testid="about-page" className="bg-ink-950">
      <Seo title="About" path="/about" description="North Peak Facility Services — a Montreal-based professional cleaning and facility services company serving homes and businesses across the Greater Montreal Area." keywords="Cleaning Company Montreal" />
      <PageHero
        overline={t("About North Peak")}
        title={t("A facility partner engineered for the long term.")}
        subtitle={t("We built North Peak on a simple belief: commercial spaces perform better when they're cared for with enterprise discipline and genuine craft.")}
        image={IMAGES.glassCurve}
      />

      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-16 items-center">
          <Reveal>
            <div className="relative aspect-[4/5] overflow-hidden rounded-4xl border border-white/8">
              <img src={IMAGES.team} alt="North Peak team" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink-950/40 to-transparent" />
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="overline text-steel-light mb-5">{t("Our Mission")}</p>
            <h2 className="font-serif text-gradient text-3xl md:text-4xl leading-tight mb-6" style={{ fontWeight: 500 }}>
              {t("Reliability you can build a business on.")}
            </h2>
            <div className="space-y-5 text-white/55 leading-relaxed text-lg">
              <p>{t("North Peak Facility Services delivers commercial cleaning and facility support that meets the standards of the most demanding environments — from Class-A office towers to medical clinics and industrial plants.")}</p>
              <p>{t("Every engagement is backed by vetted, insured crews, dedicated account management, and a quality assurance program that turns \"clean\" into a measurable, repeatable outcome.")}</p>
            </div>
            <Link to="/quote" className="group inline-flex items-center gap-2 mt-8 text-white font-600 text-sm">
              {t("Work with us")} <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="py-24 md:py-32 relative overflow-hidden glow">
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {WHY.map((w, i) => (
              <Reveal key={w.label} delay={i * 0.1}>
                <div className="glass card-hover rounded-3xl p-8 md:p-9 h-full">
                  <div className="font-serif text-4xl md:text-5xl text-gradient" style={{ fontWeight: 500 }}>{w.stat}</div>
                  <div className="mt-4 font-600 text-sm text-white">{t(w.label)}</div>
                  <p className="mt-2 text-white/45 text-sm leading-relaxed">{t(w.desc)}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-ink-900 py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal><p className="overline text-steel-light mb-5">{t("How We Work")}</p></Reveal>
          <Reveal delay={0.1}><h2 className="font-serif text-gradient text-3xl md:text-5xl leading-tight mb-14 max-w-2xl" style={{ fontWeight: 500 }}>{t("A proven method, applied to every facility.")}</h2></Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {PROCESS.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.08}>
                <div className="glass card-hover rounded-3xl p-8 h-full">
                  <div className="font-serif text-4xl text-steel/50" style={{ fontWeight: 400 }}>{p.n}</div>
                  <h3 className="mt-4 font-serif text-xl text-white" style={{ fontWeight: 500 }}>{t(p.title)}</h3>
                  <p className="mt-3 text-white/50 text-sm leading-relaxed">{t(p.desc)}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
